import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Stack,
  Tab,
  Tabs,
  IconButton,
  Tooltip,
  TableCell,
} from "@mui/material";
import { Sick, InfoOutlined } from "@mui/icons-material";
import { GridExpandMoreIcon } from "@mui/x-data-grid";

import dayjs from "dayjs";
import { GenericTable } from "../../../../../components/GenericTable/GenericTable";
import { useDatetime } from "../../../../../utils/useDatetime";
import { getMedicationRequestStatusLabel } from "../../../../../utils/useDict";
import { PrescriptionsActions } from "./PrescriptionsActions";
import { ReactNode, SyntheticEvent, useState } from "react";
import { JustificationModal } from "./JustificationModal";
import { usePreferMobileCards } from "../../../context/EhrRecordsLayoutContext";
import { EhrSummaryMobileSection } from "../../mobile/EhrSummaryMobileSection";
import {
  EhrMobilePillTabs,
  EhrResponsiveTable,
} from "../../mobile/EhrResponsiveTable";

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ maxHeight: "60vh", pt: 2, overflow: "auto" }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export function Prescriptions({
  data,
  documentReference,
  title,
  handleOpen,
  setBinaryUrl,
  openVisualizer,
  verifyVisualizer,
}: any) {
  const { formatToDDMMYYYY } = useDatetime;
  const [selectedTab, setSelectedTab] = useState(0);
  const [openJustification, setOpenJustification] = useState(false);
  const [selectedJustification, setSelectedJustification] = useState<{
    note?: string;
    status?: string;
  }>({});

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleOpenJustification = (note?: string, status?: string) => {
    setSelectedJustification({ note, status });
    setOpenJustification(true);
  };

  const handleCloseJustification = () => {
    setOpenJustification(false);
    setSelectedJustification({});
  };

  const prescriptionData = data
    ?.filter((el: any) => el?.resource?.requester)
    ?.map((el: any, index: number) => {
      const docRef = documentReference?.find((doc: any) => {
        const findRef = doc?.resource?.basedOn?.find((basedOn: any) =>
          basedOn?.reference.includes(el.resource.id)
        );
        return findRef;
      });

      const informationSource = el?.resource?.informationSource?.find(
        (source: any) => source?.reference?.includes("Practitioner")
      )?.display;
      const auxPractitioner = docRef?.resource?.contained?.find(
        (contained: any) => contained?.resourceType === "Practitioner"
      );

      const auxSpecialty =
        docRef?.resource?.practiceSetting?.coding?.[0]?.display;

      const status = el?.resource?.status ?? "";

      const description = el.resource?.medication?.concept?.text || "";
      const date = el?.resource?.effectiveDosePeriod?.start
        ? formatToDDMMYYYY(dayjs(el?.resource?.effectiveDosePeriod?.start))
        : "-";
      const binaryUrl = docRef?.resource?.content?.[0]?.attachment?.url || "";
      // Busca a nota do paciente: se note é array, procura por authorReference Patient; senão usa a primeira nota
      const note = Array.isArray(el?.resource?.note) 
        ? el?.resource?.note?.find((noteItem: any) => 
            noteItem?.authorReference?.reference?.includes('Patient')
          )?.text || "-"
        : el?.resource?.note?.[0]?.text || "-";

      const dosage = el?.resource?.dosageInstruction?.[0];
      const auxType = dosage?.doseAndRate?.[0]?.type?.text;
      const auxQtd = dosage?.doseAndRate?.[0]?.doseQuantity;
      const auxUnit = auxQtd?.unit;
      const auxUnitValue = auxQtd?.value;

      const auxTiming = dosage?.timing?.repeat;
      const auxDuration = auxTiming?.duration;
      const auxPeriod = auxTiming?.period;

      const sign =
        el?.resource?.securityLabel?.[0]?.coding?.[0]?.code === "ProofOfOrigin";

      return {
        encounterId: el?.resource?.encounter?.reference?.split("/")?.[1],
        status,
        statusLabel: getMedicationRequestStatusLabel(status),
        description,
        date,
        sign,
        dose: `${
          auxQtd?.unit && auxQtd?.value && auxType
            ? `${auxUnitValue && `${auxUnitValue}`} ${auxUnit && `${auxUnit}`}`
            : ""
        } ${
          auxPeriod
            ? `${auxPeriod && `a cada ${auxPeriod} h`} ${
                auxDuration
                  ? `durante ${auxDuration} dia(s)`
                  : "por tempo indeterminado"
              }`
            : "-"
        }`,
        specialty: auxSpecialty || "-",
        binaryUrl,
        requesterId: el?.resource?.requester?.reference,
        note,
        practitioner:
          informationSource ||
          auxPractitioner?.name?.[0]?.given?.[0] ||
          auxPractitioner?.identifier?.[0]?.value ||
          el?.resource?.requester?.reference?.display ||
          el?.resource?.requester?.reference?.split("-")?.[2],
      };
    })
    // ?.filter((row: any) => (selectedTab === 0 ? row?.binaryUrl : true))
    ?.sort((a: any, b: any) => {
      const dateA = dayjs(a.date, "DD/MM/YYYY");
      const dateB = dayjs(b.date, "DD/MM/YYYY");
      return dateB.diff(dateA);
    });


  const columns = [
    {
      column: "Medicamentos",
      rowName: "description",
    },
    { column: "Posologia", rowName: "dose" },
    { column: "Profissional", rowName: "practitioner" },
    { column: "Data", rowName: "date" },
    { column: "Status", rowName: "statusLabel" },
  ];

  const declaratedData = data
    ?.filter((el: any) => !el?.resource?.requester)
    ?.map((el: any, index: number) => {
      const docRef = documentReference?.find((doc: any) => {
        const findRef = doc?.resource?.basedOn?.find((basedOn: any) =>
          basedOn?.reference.includes(el.resource.id)
        );
        return findRef;
      });

      const informationSource = el?.resource?.informationSource?.find(
        (source: any) => source?.reference?.includes("Practitioner")
      )?.display;
      const auxPractitioner = docRef?.resource?.contained?.find(
        (contained: any) => contained?.resourceType === "Practitioner"
      );

      const auxSpecialty =
        docRef?.resource?.practiceSetting?.coding?.[0]?.display;

      const status = el?.resource?.status ?? "";

      const description = el.resource?.medication?.concept?.text || "";
      const date = el?.resource?.effectiveDosePeriod?.start
        ? formatToDDMMYYYY(dayjs(el?.resource?.effectiveDosePeriod?.start))
        : "-";
      const binaryUrl = docRef?.resource?.content?.[0]?.attachment?.url || "";
      // Busca a nota do paciente: se note é array, procura por authorReference Patient; senão usa a primeira nota
      const note = Array.isArray(el?.resource?.note) 
        ? el?.resource?.note?.find((noteItem: any) => 
            noteItem?.authorReference?.reference?.includes('Patient')
          )?.text || "-"
        : el?.resource?.note?.[0]?.text || "-";

      const dosage = el?.resource?.dosageInstruction?.[0];
      const auxType = dosage?.doseAndRate?.[0]?.type?.text;
      const auxQtd = dosage?.doseAndRate?.[0]?.doseQuantity;
      const auxUnit = auxQtd?.unit;
      const auxUnitValue = auxQtd?.value;

      const auxTiming = dosage?.timing?.repeat;
      const auxDuration = auxTiming?.duration;
      const auxPeriod = auxTiming?.period;

      return {
        status,
        statusLabel: getMedicationRequestStatusLabel(status),
        description,
        date,
        dose: `${
          auxQtd?.unit && auxQtd?.value && auxType
            ? `${auxUnitValue && `${auxUnitValue}`} ${auxUnit && `${auxUnit}`}`
            : ""
        } ${
          auxPeriod
            ? `${auxPeriod && `a cada ${auxPeriod} h`} ${
                auxDuration
                  ? `durante ${auxDuration} dia(s)`
                  : "por tempo indeterminado"
              }`
            : "-"
        }`,
        specialty: auxSpecialty || "-",
        binaryUrl,
        requesterId: el?.resource?.requester?.reference,
        note,
        practitioner:
          informationSource ||
          auxPractitioner?.name?.[0]?.given?.[0] ||
          auxPractitioner?.identifier?.[0]?.value ||
          el?.resource?.requester?.reference?.display ||
          el?.resource?.requester?.reference?.split("-")?.[2],
      };
    })
    ?.sort((a: any, b: any) => {
      const dateA = dayjs(a.date, "DD/MM/YYYY");
      const dateB = dayjs(b.date, "DD/MM/YYYY");
      return dateB.diff(dateA);
    });

  const isEhrMobile = usePreferMobileCards();

  const renderPrescriptionActions = (rowData: any) => {
    const actions = (
      <PrescriptionsActions
        verifyVisualizer={verifyVisualizer}
        openVisualizer={openVisualizer}
        row={rowData}
        handleOpen={handleOpen}
        setBinaryUrl={setBinaryUrl}
        onOpenJustification={handleOpenJustification}
      />
    );

    if (isEhrMobile) return actions;

    return (
      <TableCell align="center">
        <Stack direction="row" spacing={1} justifyContent="center">
          {actions}
        </Stack>
      </TableCell>
    );
  };

  const prescribedTable = (
    <EhrResponsiveTable
      title="Medicamentos prescritos"
      hideSectionHeader
      columns={columns}
      rows={prescriptionData ?? []}
      actions={renderPrescriptionActions}
    />
  );

  const declaredTable = (
    <EhrResponsiveTable
      title="Medicamentos declarados"
      hideSectionHeader
      columns={columns?.filter((e: any) => e?.rowName !== "practitioner")}
      rows={declaratedData ?? []}
      actions={renderPrescriptionActions}
    />
  );

  if (isEhrMobile) {
    return (
      <>
        <EhrSummaryMobileSection
          title={title}
          subtitle={`${(prescriptionData?.length ?? 0) + (declaratedData?.length ?? 0)} registro(s)`}
        >
          <EhrMobilePillTabs
            tabs={[
              {
                value: "0",
                label: `Prescritos (${prescriptionData?.length || 0})`,
              },
              {
                value: "1",
                label: `Declarados (${declaratedData?.length || 0})`,
              },
            ]}
            value={String(selectedTab)}
            onChange={(value) => setSelectedTab(Number(value))}
          />
          {selectedTab === 0 ? prescribedTable : declaredTable}
        </EhrSummaryMobileSection>

        <JustificationModal
          open={openJustification}
          onClose={handleCloseJustification}
          note={selectedJustification.note}
          status={selectedJustification.status}
        />
      </>
    );
  }

  return (
    <>
    <Accordion sx={{ width: "100%" }} elevation={5}>
      <AccordionSummary
        expandIcon={<GridExpandMoreIcon />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <Stack direction="row" alignItems="center" gap={1}>
          {title}
          <Avatar
            sx={{
              width: 26,
              height: 26,
              fontSize: "14px",
              fontWeight: 600,
              backgroundColor: "primary100.main",
              color: "primary1000.main",
            }}
          >
            {data?.length || "0"}
          </Avatar>
        </Stack>
      </AccordionSummary>

      <AccordionDetails>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={selectedTab}
            onChange={handleChange}
            aria-label="basic tabs example"
            variant="scrollable"
          >
            <Tab
              label={
                <Box style={{ display: "flex", alignItems: "center" }}>
                  Prescritos
                  <Avatar
                    sx={{
                      width: 26,
                      marginLeft: 1,
                      height: 26,
                      fontSize: "14px",
                      fontWeight: 600,
                      backgroundColor: "primary100.main",
                      color: "primary1000.main",
                    }}
                  >
                    {prescriptionData?.length || "0"}
                  </Avatar>
                </Box>
              }
              {...a11yProps(0)}
            />
            <Tab
              label={
                <Box style={{ display: "flex", alignItems: "center" }}>
                  Declarados pelo paciente
                  <Avatar
                    sx={{
                      width: 26,
                      marginLeft: 1,
                      height: 26,
                      fontSize: "14px",
                      fontWeight: 600,
                      backgroundColor: "primary100.main",
                      color: "primary1000.main",
                    }}
                  >
                    {declaratedData?.length || "0"}
                  </Avatar>
                </Box>
              }
              {...a11yProps(1)}
            />
          </Tabs>
        </Box>

        <CustomTabPanel value={selectedTab} index={0}>
          {prescribedTable}
        </CustomTabPanel>

        <CustomTabPanel value={selectedTab} index={1}>
          {declaredTable}
        </CustomTabPanel>
      </AccordionDetails>
    </Accordion>

      <JustificationModal
        open={openJustification}
        onClose={handleCloseJustification}
        note={selectedJustification.note}
        status={selectedJustification.status}
      />
    </>
  );
}
